import React from "react";
import styled from "@emotion/styled";

import UserInitials, { UnassignedIcon } from "../../../components/comments/UserInitials";
import { parseDateValue } from "../../../lib/columnSort";
import { EllipsisTooltipTextLink } from "../../../lib/core_components/EllipsisTooltip";
import { OpportunityFromSearch } from "../../../lib/hooks/api/opportunities/useOpportunities";
import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { formatDate } from "../../../lib/utils";
import { Flex } from "../../../styles/utility-components";
import SuppliersContainer from "../../table_components/SuppliersContainer";

export type RenderCellProps = {
  opportunity: OpportunityFromSearch;
  minWidth?: number;
};

export const renderBuyerCell = ({ opportunity, minWidth }: RenderCellProps): JSX.Element => {
  if (opportunity.buyers.length === 0) {
    return <TableCell minWidth={minWidth}>-</TableCell>;
  }

  const buyer = opportunity.buyers[0];
  return (
    <TableCell>
      <Flex alignItems="center" gap={8} minWidth={minWidth}>
        {buyer.logoUrl ? (
          <LogoImage src={buyer.logoUrl} alt={`${buyer.name} logo`} />
        ) : (
          <EmptyLogoImage>
            <UIcon icon="building" size={12} />
          </EmptyLogoImage>
        )}
        <EllipsisTooltipTextLink
          fullText={buyer.name ?? "--"}
          linkText={buyer.name ?? "--"}
          linkProps={{
            to: `/buyers/${buyer.id}`,
          }}
        />
      </Flex>
    </TableCell>
  );
};

export const renderAssignedToCell = ({ opportunity, minWidth }: RenderCellProps): JSX.Element => {
  if (!opportunity.assignedTo) {
    return (
      <TableCell minWidth={minWidth}>
        <UnassignedContainer>
          <UnassignedIcon type="small" />
          {"-"}
        </UnassignedContainer>
      </TableCell>
    );
  }

  const { firstName, lastName } = opportunity.assignedTo;
  return (
    <TableCell minWidth={minWidth}>
      <AssignedUserContainer>
        <UserInitials firstName={firstName} lastName={lastName} type="small" />
        <AssignedUserName>{`${firstName} ${lastName}`}</AssignedUserName>
      </AssignedUserContainer>
    </TableCell>
  );
};

export const renderDateCell = (date: string | null | undefined, minWidth: number): JSX.Element => {
  if (!date) return <TableCell minWidth={minWidth}>-</TableCell>;

  const parsedDate = parseDateValue(date);
  return (
    <TableCell minWidth={minWidth}>
      {!parsedDate.isValid ? "-" : formatDate(parsedDate.toJSDate())}
    </TableCell>
  );
};

export const renderSupplierCell = ({ opportunity }: RenderCellProps): JSX.Element => {
  if (opportunity.suppliers.length === 0) {
    return <TableCell>-</TableCell>;
  }

  const supplier = opportunity.suppliers[0];
  const mappedSuppliers = opportunity.suppliers.map((s) => ({
    guid: s.id,
    name: s.name,
  }));

  return (
    <TableCell>
      <Flex alignItems="center" gap={8}>
        {supplier.logoUrl ? (
          <LogoImage src={supplier.logoUrl} alt={`${supplier.name} logo`} />
        ) : (
          <EmptyLogoImage>
            <UIcon icon="trophy01" size={12} />
          </EmptyLogoImage>
        )}
        <SuppliersContainer recordName={opportunity.name ?? "--"} suppliers={mappedSuppliers} />
      </Flex>
    </TableCell>
  );
};

export const AssignedUserContainer = styled(Flex)({
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
});

export const AssignedUserName = styled.span({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const UnassignedContainer = styled(Flex)({
  alignItems: "center",
  gap: "8px",
});

interface TableCellProps {
  minWidth?: number;
}

export const TableCell = styled.div<TableCellProps>(({ minWidth }) => ({
  display: "grid",
  minWidth: minWidth ? `${minWidth}px` : "auto",
  margin: 0,
  padding: 0,
}));

export const LogoImage = styled.img({
  width: 16,
  height: 16,
  objectFit: "contain",
  borderRadius: 4,
});

export const EmptyLogoImage = styled.div(({ theme }) => ({
  width: 16,
  height: 16,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 2,
  color: theme.colors.sysTextDefault,
  backgroundColor: theme.colors.sysBorderDecorative,
  borderRadius: 4,
}));
